import type {
  AdminCreateOrgInput,
  AdminUpdateOrgInput,
  ApproveOrgRequestInput,
  CreateInviteCodeInput,
  CreateOrgInput,
  CreatorGrantInput,
  Answer,
  FrozenQuestion,
  LimitKey,
  QuestionBankInput,
  QuestionInput,
  QuizInput,
  QuizMode,
  QuizResult,
  QuizSettings,
  Skill,
  TakerQuestion,
  Limits,
  OrgRequestInput,
  OrgType,
  Plan,
  PlanId,
  Role,
} from '@ready/shared';
import { yearMonthIn } from '@ready/shared';
import { useAuth } from './auth';
import { browserTimeZone } from './dates';

const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Set when a plan limit blocked the request. */
    readonly limit?: LimitKey,
  ) {
    super(message);
  }
}

export interface NameStatus {
  certificates: number;
  /** Self-service corrections left once certificates exist (0 or 1). */
  correctionsLeft: number;
  /** True when only a platform admin can change the name. */
  locked: boolean;
}
export interface Me {
  id: string;
  email: string;
  displayName: string | null;
  /** IANA zone saved on the profile; null until the app first reports one. */
  timeZone: string | null;
  isPlatformAdmin: boolean;
  name: NameStatus;
  /** null: must request an org. */
  creator:
    | { unlimited: true; maxOrgs: null; used: null; planId: null }
    | { unlimited: false; maxOrgs: number; used: number; planId: PlanId }
    | null;
}
export interface TreeUsage {
  rootOrgId: string;
  rootOrgName: string;
  planId: PlanId;
  planName: string;
  limits: Limits;
  usage: Limits;
}
export type OrgRequestStatus = 'pending' | 'approved' | 'rejected' | 'canceled';
export interface OrgRequest {
  id: string;
  name: string;
  type: OrgType;
  country: string;
  website: string | null;
  domain: string | null;
  expectedMembers: number;
  message: string | null;
  status: OrgRequestStatus;
  decisionNote: string | null;
  orgId: string | null;
  createdAt: string;
  decidedAt: string | null;
}
export interface AdminOrgRequest extends OrgRequest {
  requesterEmail: string;
  signals: { emailMatchesDomain: boolean; domainTakenBy: string | null };
}
export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  isPlatformAdmin: boolean;
  suspendedAt: string | null;
  createdAt: string;
  creatorGrant: { maxOrgs: number; planId: PlanId; reason: string | null } | null;
}
export interface AdminOrg extends Org, TreeUsage {
  archivedAt: string | null;
  limitOverrides: Partial<Limits> | null;
  createdAt: string;
}
export interface Question extends Omit<FrozenQuestion, 'explanation'> {
  explanation: string | null;
  bankId: string;
  ownerOrgId: string | null;
  createdAt: string;
}
export interface QuestionBank {
  id: string;
  ownerOrgId: string | null;
  title: string;
  description: string | null;
}
export interface QuestionBankSummary extends QuestionBank {
  questionCount: number;
  /** False for Nirvik library banks shown to an org (read-only). */
  editable: boolean;
}
/** A question as listed in a quiz draft, with the bank it came from. */
export interface QuizQuestionRef {
  id: string;
  prompt: string;
  skill: Skill;
  bankId: string;
  bankTitle: string;
}
export interface Quiz {
  id: string;
  ownerOrgId: string | null;
  slug: string;
  title: string;
  description: string | null;
  mode: QuizMode;
  audience: 'public' | 'org';
  settings: QuizSettings;
  status: 'draft' | 'published' | 'archived';
  updatedAt: string;
}
export interface PublicQuiz {
  slug: string;
  title: string;
  description: string | null;
  mode: QuizMode;
  questionCount: number;
  passPercent: number;
  certificate: boolean;
  maxAttempts: number | null;
  issuerName: string;
  audience: 'public' | 'org';
}
export interface StartedAttempt {
  attemptId: string;
  quiz: {
    title: string;
    description: string | null;
    mode: QuizMode;
    issuerName: string;
    passPercent: number;
  };
  questions: TakerQuestion[];
}
/** What a taker sees after finishing: score and skills, never questions or answers. */
export interface AttemptResult {
  quiz: {
    title: string;
    mode: QuizMode;
    passPercent: number;
    slug: string;
    audience: 'public' | 'org';
  };
  result: QuizResult;
  certificateCode: string | null;
}
/** Self-assessment feedback: right or wrong; an explanation only when right. */
export interface AnswerFeedback {
  correct: boolean;
  explanation: string | null;
  skill: Skill;
}
export interface LearnerQuiz {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  mode: QuizMode;
  from: string;
  attempts: number;
  best: { attemptId: string; percent: number; passed: boolean } | null;
}
export interface Certificate {
  id: string;
  code: string;
  title: string;
  recipientName: string;
  issuerName: string;
  percent: number;
  isPublic: boolean;
  issuedAt: string;
  /** The learner's zone at issue; the issue date is shown in it. */
  timeZone: string;
}
export interface QuizReport {
  quizId: string;
  title: string;
  /** Who made the quiz: this or another org, or Nirvik Ready. */
  from: string;
  certificates: number;
  attempts: number;
  learners: number;
  averagePercent: number;
  passRate: number;
  skills: Partial<Record<Skill, number>>;
}
/** Where authored content lives: an org's bank, or Nirvik's library. */
export type ContentOwner = { kind: 'org'; orgId: string } | { kind: 'library' };
const contentBase = (o: ContentOwner) =>
  o.kind === 'org' ? `/v1/orgs/${o.orgId}` : '/v1/admin/library';

export interface AuditEntry {
  log: {
    id: string;
    action: string;
    targetType: string | null;
    targetId: string | null;
    details: unknown;
    createdAt: string;
  };
  actorEmail: string | null;
  orgName: string | null;
}

export interface Org {
  id: string;
  name: string;
  type: OrgType;
  parentId: string | null;
  depth: number;
  country: string;
  joinPolicy: 'open' | 'approval' | 'invite_only' | 'managed';
}
export interface MyOrg extends Pick<Org, 'id' | 'name' | 'type' | 'parentId' | 'depth'> {
  role: Role;
}
export interface Member {
  userId: string;
  email: string;
  displayName: string | null;
  orgId: string;
  role: Role;
  source: string;
  joinedAt: string;
}
export interface OrgDomain {
  id: string;
  domain: string;
  verifiedAt: string | null;
  record: { name: string; value: string };
}
export interface InviteCode {
  id: string;
  code: string;
  role: Role;
  maxUses: number | null;
  uses: number;
  expiresAt: string | null;
  createdAt: string;
}

export type CertificateFileFormat = 'pdf' | 'png';

/** A public certificate's PDF or PNG (plain link; no account needed). */
export const publicCertificateFileUrl = (code: string, format: CertificateFileFormat) =>
  `${BASE}/v1/public/certificates/${code}/${format}`;

/** Its public verification page. */
export const certificateVerifyUrl = (code: string) => `${window.location.origin}/verify/${code}`;

/**
 * LinkedIn's share dialog for the verification page. LinkedIn reads the
 * page's preview tags (served by the API) to show the certificate image.
 */
export const linkedInShareUrl = (code: string) =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(certificateVerifyUrl(code))}`;

/** A quiz's public page, for sharing. */
export const quizUrl = (slug: string) => `${window.location.origin}/q/${slug}`;

/** LinkedIn's share dialog for a quiz page (the API serves its preview tags and image). */
export const linkedInShareQuizUrl = (slug: string) =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(quizUrl(slug))}`;

/**
 * LinkedIn's "Add licenses & certifications" form, pre-filled with this
 * certificate. The issuing organization is always Nirvik AI Labs, the legal
 * entity behind every certificate (also on its seal), even for org quizzes.
 */
export function linkedInAddToProfileUrl(
  c: Pick<Certificate, 'code' | 'title' | 'issuedAt' | 'timeZone'>,
): string {
  const issued = yearMonthIn(c.issuedAt, c.timeZone);
  return `https://www.linkedin.com/profile/add?${new URLSearchParams({
    startTask: 'CERTIFICATION_NAME',
    name: c.title,
    organizationName: 'Nirvik AI Labs',
    issueYear: String(issued.year),
    issueMonth: String(issued.month),
    certUrl: certificateVerifyUrl(c.code),
    certId: c.code,
  })}`;
}

export type PosterTheme = 'light' | 'dark';
export type PaperSize = 'letter' | 'a4';

/** Saves a downloaded file under the given name. */
export function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** API client bound to the current session's auth headers. */
export function useApi() {
  const { authHeaders } = useAuth();

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        ...(await authHeaders()),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string; limit?: LimitKey };
      throw new ApiError(data.error ?? `Request failed (${res.status})`, res.status, data.limit);
    }
    return (await res.json()) as T;
  }

  /** A binary response (PDF, PNG), fetched with the session's auth headers. */
  async function file(path: string): Promise<Blob> {
    const res = await fetch(`${BASE}${path}`, { headers: await authHeaders() });
    if (!res.ok) throw new ApiError(`Download failed (${res.status})`, res.status);
    return res.blob();
  }

  return {
    me: () => request<Me>('GET', '/v1/me'),
    setTimeZone: (timeZone: string) =>
      request<{ timeZone: string }>('PUT', '/v1/me/time-zone', { timeZone }),
    updateMe: (displayName: string) =>
      request<{ id: string; displayName: string } & NameStatus>('PATCH', '/v1/me', { displayName }),
    learning: () => request<{ quizzes: LearnerQuiz[] }>('GET', '/v1/me/learning'),
    certificates: () => request<{ certificates: Certificate[] }>('GET', '/v1/me/certificates'),
    /** Downloads one of your certificates as a PDF or PNG, public or private. */
    downloadCertificate: async (code: string, format: CertificateFileFormat) =>
      saveBlob(
        await file(`/v1/me/certificates/${code}/${format}`),
        `Nirvik-Ready-Certificate-${code}.${format}`,
      ),
    /** A quiz's campaign poster (org quizzes need membership, hence the auth headers). */
    posterFile: (
      slug: string,
      theme: PosterTheme,
      format: 'pdf' | 'png',
      size: PaperSize = 'letter',
    ) => file(`/v1/public/quizzes/${slug}/poster/${theme}/${format}?size=${size}`),
    setCertificatePublic: (id: string, isPublic: boolean) =>
      request<Certificate>('PATCH', `/v1/me/certificates/${id}`, { isPublic }),
    // Taking quizzes: the details are public; starting needs an account.
    publicQuiz: (slug: string) => request<PublicQuiz>('GET', `/v1/public/quizzes/${slug}`),
    startAttempt: (slug: string) => request<StartedAttempt>('POST', '/v1/attempts', { slug }),
    checkAnswer: (attemptId: string, answer: Answer) =>
      request<AnswerFeedback>('POST', `/v1/attempts/${attemptId}/check`, answer),
    submitAttempt: (attemptId: string, answers: Answer[]) =>
      request<{ result: QuizResult; certificateCode: string | null }>(
        'POST',
        `/v1/attempts/${attemptId}/submit`,
        // The zone fixes the issue date on any certificate this earns.
        { answers, timeZone: browserTimeZone() },
      ),
    attempt: (attemptId: string) => request<AttemptResult>('GET', `/v1/attempts/${attemptId}`),
    verifyCertificate: (code: string) =>
      request<
        Pick<
          Certificate,
          'code' | 'title' | 'recipientName' | 'issuerName' | 'percent' | 'issuedAt' | 'timeZone'
        >
      >('GET', `/v1/public/certificates/${code}`),
    // Authoring.
    banks: (o: ContentOwner) =>
      request<{ banks: QuestionBankSummary[] }>('GET', `${contentBase(o)}/question-banks`),
    createBank: (o: ContentOwner, input: QuestionBankInput) =>
      request<QuestionBank>('POST', `${contentBase(o)}/question-banks`, input),
    bank: (id: string, q = '') =>
      request<{ bank: QuestionBank; questions: Question[] }>(
        'GET',
        `/v1/question-banks/${id}${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
    updateBank: (id: string, input: QuestionBankInput) =>
      request<QuestionBank>('PUT', `/v1/question-banks/${id}`, input),
    archiveBank: (id: string) => request<{ ok: true }>('DELETE', `/v1/question-banks/${id}`),
    createQuestion: (bankId: string, input: QuestionInput) =>
      request<Question>('POST', `/v1/question-banks/${bankId}/questions`, input),
    question: (id: string) => request<Question>('GET', `/v1/questions/${id}`),
    updateQuestion: (id: string, input: QuestionInput) =>
      request<Question>('PUT', `/v1/questions/${id}`, input),
    archiveQuestion: (id: string) => request<{ ok: true }>('DELETE', `/v1/questions/${id}`),
    quizzes: (o: ContentOwner) => request<{ quizzes: Quiz[] }>('GET', `${contentBase(o)}/quizzes`),
    createQuiz: (o: ContentOwner, input: Partial<QuizInput> & { title: string }) =>
      request<Quiz>('POST', `${contentBase(o)}/quizzes`, input),
    quiz: (id: string) =>
      request<Quiz & { questions: QuizQuestionRef[] }>('GET', `/v1/quizzes/${id}`),
    updateQuiz: (id: string, input: QuizInput) => request<Quiz>('PUT', `/v1/quizzes/${id}`, input),
    publishQuiz: (id: string) => request<{ version: number }>('POST', `/v1/quizzes/${id}/publish`),
    archiveQuiz: (id: string, archived: boolean) =>
      request<Quiz>('POST', `/v1/quizzes/${id}/archive`, { archived }),
    quizReport: (orgId: string) =>
      request<{ quizzes: QuizReport[] }>('GET', `/v1/orgs/${orgId}/reports/quizzes`),
    myOrgRequests: () => request<{ requests: OrgRequest[] }>('GET', '/v1/me/org-requests'),
    requestOrg: (input: OrgRequestInput) =>
      request<OrgRequest>('POST', '/v1/me/org-requests', input),
    cancelOrgRequest: (id: string) =>
      request<{ ok: true }>('POST', `/v1/me/org-requests/${id}/cancel`),
    usage: (orgId: string) => request<TreeUsage>('GET', `/v1/orgs/${orgId}/usage`),
    myOrgs: () => request<{ orgs: MyOrg[] }>('GET', '/v1/me/orgs'),
    createOrg: (input: CreateOrgInput) => request<Org>('POST', '/v1/orgs', input),
    org: (orgId: string) => request<Org>('GET', `/v1/orgs/${orgId}`),
    updateOrg: (orgId: string, input: Partial<Pick<Org, 'name' | 'joinPolicy'>>) =>
      request<Org>('PATCH', `/v1/orgs/${orgId}`, input),
    children: (orgId: string) => request<{ orgs: Org[] }>('GET', `/v1/orgs/${orgId}/children`),
    createChild: (orgId: string, input: CreateOrgInput) =>
      request<Org>('POST', `/v1/orgs/${orgId}/children`, input),
    members: (orgId: string, descendants = false) =>
      request<{ members: Member[] }>(
        'GET',
        `/v1/orgs/${orgId}/members${descendants ? '?descendants=true' : ''}`,
      ),
    domains: (orgId: string) =>
      request<{ domains: OrgDomain[] }>('GET', `/v1/orgs/${orgId}/domains`),
    addDomain: (orgId: string, domain: string) =>
      request<OrgDomain>('POST', `/v1/orgs/${orgId}/domains`, { domain }),
    verifyDomain: (orgId: string, domainId: string) =>
      request<{ verified: boolean }>('POST', `/v1/orgs/${orgId}/domains/${domainId}/verify`),
    inviteCodes: (orgId: string) =>
      request<{ codes: InviteCode[] }>('GET', `/v1/orgs/${orgId}/invite-codes`),
    revokeInviteCode: (orgId: string, codeId: string) =>
      request<{ ok: true }>('DELETE', `/v1/orgs/${orgId}/invite-codes/${codeId}`),
    createInviteCode: (orgId: string, input: Partial<CreateInviteCodeInput>) =>
      request<InviteCode>('POST', `/v1/orgs/${orgId}/invite-codes`, input),
    admin: {
      overview: () =>
        request<{ pendingRequests: number; users: number; topLevelOrgs: number }>(
          'GET',
          '/v1/admin/overview',
        ),
      plans: () => request<{ plans: Plan[] }>('GET', '/v1/admin/plans'),
      orgRequests: (status: OrgRequestStatus | 'all') =>
        request<{ requests: AdminOrgRequest[] }>('GET', `/v1/admin/org-requests?status=${status}`),
      approve: (id: string, input: Partial<ApproveOrgRequestInput>) =>
        request<{ org: Org }>('POST', `/v1/admin/org-requests/${id}/approve`, input),
      reject: (id: string, note: string) =>
        request<{ ok: true }>('POST', `/v1/admin/org-requests/${id}/reject`, { note }),
      users: (q = '') =>
        request<{ users: AdminUser[] }>(
          'GET',
          `/v1/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        ),
      setCreatorGrant: (userId: string, input: Partial<CreatorGrantInput> & { maxOrgs: number }) =>
        request('PUT', `/v1/admin/users/${userId}/creator-grant`, input),
      revokeCreatorGrant: (userId: string) =>
        request('DELETE', `/v1/admin/users/${userId}/creator-grant`),
      setPlatformAdmin: (userId: string, isAdmin: boolean) =>
        request('PUT', `/v1/admin/users/${userId}/platform-admin`, { isAdmin }),
      setDisplayName: (userId: string, displayName: string) =>
        request('PUT', `/v1/admin/users/${userId}/display-name`, { displayName }),
      setSuspended: (userId: string, suspended: boolean) =>
        request('PUT', `/v1/admin/users/${userId}/suspended`, { suspended }),
      orgs: (q = '') =>
        request<{ orgs: AdminOrg[] }>(
          'GET',
          `/v1/admin/orgs${q ? `?q=${encodeURIComponent(q)}` : ''}`,
        ),
      createOrg: (
        input: Partial<AdminCreateOrgInput> &
          Pick<AdminCreateOrgInput, 'name' | 'type' | 'country' | 'ownerEmail'>,
      ) => request<{ org: Org; ownerInvited: boolean }>('POST', '/v1/admin/orgs', input),
      updateOrg: (orgId: string, input: AdminUpdateOrgInput) =>
        request<Org>('PATCH', `/v1/admin/orgs/${orgId}`, input),
      auditLogs: () => request<{ logs: AuditEntry[] }>('GET', '/v1/admin/audit-logs'),
    },
    join: (code: string) =>
      request<{ orgId: string; role: Role }>('POST', `/v1/join/${encodeURIComponent(code)}`),
  };
}
